// Shared loading / empty / error presentation for Firestore-backed sections.
//
// The project scope requires that empty-data and error states are handled
// cleanly and that a backend error never breaks the frontend. These components
// keep that consistent across every section instead of each one inventing its
// own treatment.
//
// Styling follows the site's hand-rolled Tailwind conventions (card-light,
// navy/brand tokens) rather than shadcn, which is reserved for the admin panel.
import { AlertCircle, Inbox, RefreshCw } from "lucide-react";

/** Grey placeholder block. */
export function Skeleton({ className = "" }) {
  return (
    <div
      aria-hidden
      className={`animate-pulse rounded-lg bg-slate-200/70 ${className}`}
    />
  );
}

/** Card-shaped skeleton used while a grid of cards loads. */
export function CardSkeleton({ withImage = false }) {
  return (
    <div className="rounded-2xl border border-slate-100 bg-white p-6">
      {withImage && <Skeleton className="mb-5 h-40 w-full rounded-xl" />}
      <Skeleton className="h-11 w-11 rounded-xl" />
      <Skeleton className="mt-5 h-5 w-4/5" />
      <Skeleton className="mt-3 h-4 w-full" />
      <Skeleton className="mt-2 h-4 w-3/4" />
      <Skeleton className="mt-5 h-3 w-1/3" />
    </div>
  );
}

/** A grid of card skeletons matching the real grid's column count. */
export function CardGridSkeleton({ count = 3, withImage = false, className }) {
  return (
    <div
      className={
        className || "grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
      }
      data-testid="loading-skeleton"
    >
      {Array.from({ length: count }).map((_, i) => (
        <CardSkeleton key={i} withImage={withImage} />
      ))}
    </div>
  );
}

/**
 * Shown when a query succeeds but returns nothing — e.g. an admin has
 * unpublished every article, or a filter matches no rows.
 */
export function EmptyState({
  title = "Nothing here yet",
  description,
  icon: Icon = Inbox,
  light = false,
}) {
  return (
    <div
      data-testid="empty-state"
      className={`flex flex-col items-center justify-center rounded-2xl border border-dashed px-6 py-16 text-center ${
        light ? "border-white/20 bg-white/5" : "border-slate-200 bg-white"
      }`}
    >
      <span
        className={`flex h-14 w-14 items-center justify-center rounded-2xl ${
          light ? "bg-white/10 text-brand-light" : "bg-brand/10 text-brand"
        }`}
      >
        <Icon className="h-7 w-7" />
      </span>
      <p
        className={`mt-5 font-heading text-lg font-bold ${
          light ? "text-white" : "text-navy"
        }`}
      >
        {title}
      </p>
      {description && (
        <p
          className={`mt-2 max-w-sm text-sm leading-relaxed ${
            light ? "text-slate-300" : "text-slate-600"
          }`}
        >
          {description}
        </p>
      )}
    </div>
  );
}

/**
 * Shown when a query fails. Deliberately non-alarming: this is a public
 * marketing site, so it offers a retry rather than exposing error detail.
 */
export function ErrorState({
  title = "We couldn't load this content",
  description = "Something went wrong on our side. Please try again in a moment.",
  onRetry,
  light = false,
}) {
  return (
    <div
      role="alert"
      data-testid="error-state"
      className={`flex flex-col items-center justify-center rounded-2xl border px-6 py-16 text-center ${
        light ? "border-white/20 bg-white/5" : "border-amber-200 bg-amber-50"
      }`}
    >
      <span
        className={`flex h-14 w-14 items-center justify-center rounded-2xl ${
          light ? "bg-white/10 text-amber-300" : "bg-amber-100 text-amber-600"
        }`}
      >
        <AlertCircle className="h-7 w-7" />
      </span>
      <p
        className={`mt-5 font-heading text-lg font-bold ${
          light ? "text-white" : "text-navy"
        }`}
      >
        {title}
      </p>
      <p
        className={`mt-2 max-w-sm text-sm leading-relaxed ${
          light ? "text-slate-300" : "text-slate-700"
        }`}
      >
        {description}
      </p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="btn-outline-sky mt-6 !py-2.5 !text-xs"
        >
          <RefreshCw className="h-3.5 w-3.5" /> Try again
        </button>
      )}
    </div>
  );
}

/**
 * Convenience wrapper: renders the right state for a react-query result and
 * only calls `children` once real data is available.
 *
 * Sections that must never show an error box on the marketing site (e.g. a
 * testimonials strip on the homepage) can pass `hideOnError` to collapse
 * instead — a missing section reads better there than an error panel.
 */
export function QueryBoundary({
  query,
  children,
  skeleton,
  empty,
  hideOnError = false,
  light = false,
}) {
  if (query.isLoading) {
    return skeleton ?? <CardGridSkeleton />;
  }

  if (query.isError) {
    if (hideOnError) return null;
    return <ErrorState onRetry={query.refetch} light={light} />;
  }

  const data = query.data;
  const isEmpty = !data || (Array.isArray(data) && data.length === 0);

  if (isEmpty) {
    if (hideOnError && empty === undefined) return null;
    return empty ?? <EmptyState light={light} />;
  }

  return children(data);
}
